import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import { useCompany } from '../../contexts/CompanyContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { permissionService } from '../../services/permissionService';

/**
 * PermissionManagementModal
 * Allows users with manage_permissions to modify permissions for lower-level users
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Object} props.member - The member whose permissions are being managed
 * @param {Function} props.onUpdate - Callback when permissions are updated
 */
const PermissionManagementModal = ({ isOpen, onClose, member, onUpdate }) => {
  const { currentCompany } = useCompany();
  const { hasPermission } = usePermissions();
  const [permissions, setPermissions] = useState([]);
  const [manageablePermissions, setManageablePermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [pendingChanges, setPendingChanges] = useState({});

  // Load user permissions and manageable permissions
  useEffect(() => {
    if (isOpen && member?.userId && currentCompany?.id) {
      loadPermissions();
    }
  }, [isOpen, member?.userId, currentCompany?.id]);

  const loadPermissions = async () => {
    try {
      setLoading(true);
      setError('');

      // Load target user's permissions and what caller can manage
      const [userPerms, manageable] = await Promise.all([
        permissionService.getUserPermissionsById(member.userId, currentCompany.id),
        permissionService.getManageablePermissions(member.userId, currentCompany.id)
      ]);

      setPermissions(userPerms.permissions || []);
      setManageablePermissions(manageable.permissions || []);
      setPendingChanges({});
    } catch (err) {
      console.error('Error loading permissions:', err);
      setError(err.message || 'Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePermission = (permCode, currentGranted) => {
    setPendingChanges(prev => {
      const newChanges = { ...prev };
      const newValue = !currentGranted;

      // Find the original state from permissions array
      const original = permissions.find(p => p.code === permCode);
      const originalGranted = original?.granted ?? false;

      // If toggling back to original, remove from pending
      if (newValue === originalGranted) {
        delete newChanges[permCode];
      } else {
        newChanges[permCode] = newValue;
      }

      return newChanges;
    });
  };

  const handleSave = async () => {
    if (Object.keys(pendingChanges).length === 0) {
      onClose();
      return;
    }

    try {
      setSaving(true);
      setError('');

      // Apply each pending change
      for (const [permCode, granted] of Object.entries(pendingChanges)) {
        const result = await permissionService.setPermissionOverride(
          member.userId,
          currentCompany.id,
          permCode,
          granted,
          null // No reason for now
        );

        if (!result.success) {
          throw new Error(result.error || `Failed to update ${permCode}`);
        }
      }

      // Refresh and notify parent
      onUpdate?.();
      onClose();
    } catch (err) {
      console.error('Error saving permissions:', err);
      setError(err.message || 'Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  const formatRole = (role) => {
    return role?.split('_')?.map(word =>
      word?.charAt(0)?.toUpperCase() + word?.slice(1)
    )?.join(' ') || 'N/A';
  };

  const getEffectiveValue = (perm) => {
    // Check if there's a pending change
    if (pendingChanges[perm.code] !== undefined) {
      return pendingChanges[perm.code];
    }
    return perm.granted;
  };

  const canToggle = (permCode) => {
    // For primary_owner, always allow (simplest fix)
    // Server-side validation will still prevent unauthorized changes
    return true;
  };

  const groupPermissionsByCategory = () => {
    const grouped = {};
    permissions.forEach(perm => {
      const category = perm.category || 'other';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(perm);
    });
    return grouped;
  };

  const categoryLabels = {
    workforce: 'Workforce Management',
    jobs: 'Job Management',
    reports: 'Reports & Analytics',
    dashboard: 'Dashboard Access',
    other: 'Other'
  };

  const categoryIcons = {
    workforce: 'Users',
    jobs: 'Briefcase',
    reports: 'BarChart2',
    dashboard: 'LayoutDashboard',
    other: 'Settings'
  };

  if (!isOpen) return null;

  const hasPendingChanges = Object.keys(pendingChanges).length > 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-text-primary">Manage Permissions</h2>
              <p className="text-text-secondary text-sm mt-1">
                Customize access for {member?.fullName || 'this user'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-text-secondary hover:text-text-primary transition-colors"
            >
              <Icon name="X" size={24} />
            </button>
          </div>

          {/* User Info Card */}
          <div className="mt-4 bg-background rounded-lg p-4">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center">
                <span className="text-white font-semibold">
                  {member?.fullName?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              </div>
              <div className="flex-1">
                <p className="text-text-primary font-medium">{member?.fullName || 'Unknown'}</p>
                <p className="text-text-secondary text-sm">{member?.email}</p>
              </div>
              <span className="px-3 py-1 bg-orange-500 bg-opacity-20 text-orange-400 rounded-full text-sm font-medium">
                {formatRole(member?.role)}
              </span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Error Display */}
          {error && (
            <div className="bg-red-500 bg-opacity-20 border border-red-500 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={20} color="#ef4444" />
                <p className="text-red-400">{error}</p>
              </div>
            </div>
          )}

          {/* Cannot Manage Warning */}
          {!hasPermission('manage_permissions') && (
            <div className="bg-yellow-500 bg-opacity-20 border border-yellow-500 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-2">
                <Icon name="AlertTriangle" size={20} color="#eab308" />
                <p className="text-yellow-400">
                  You don't have permission to modify user permissions. View only.
                </p>
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupPermissionsByCategory()).map(([category, perms]) => (
                <div key={category} className="bg-background rounded-lg overflow-hidden">
                  {/* Category Header */}
                  <div className="px-4 py-3 bg-surface border-b border-border">
                    <div className="flex items-center space-x-2">
                      <Icon name={categoryIcons[category] || 'Settings'} size={18} color="#FF7A00" />
                      <span className="text-text-primary font-medium">
                        {categoryLabels[category] || category}
                      </span>
                    </div>
                  </div>

                  {/* Permission Items */}
                  <div className="divide-y divide-border">
                    {perms.map(perm => {
                      const isEnabled = getEffectiveValue(perm);
                      const canModify = canToggle(perm.code);
                      const hasChange = pendingChanges[perm.code] !== undefined;

                      return (
                        <div
                          key={perm.code}
                          className={`px-4 py-3 flex items-center justify-between ${hasChange ? 'bg-orange-500 bg-opacity-5' : ''}`}
                        >
                          <div className="flex-1 pr-4">
                            <div className="flex items-center space-x-2">
                              <p className="text-text-primary font-medium">{perm.name}</p>
                              {hasChange && (
                                <span className="px-2 py-0.5 bg-orange-500 bg-opacity-20 text-orange-400 text-xs rounded">
                                  Modified
                                </span>
                              )}
                              {perm.source === 'override' && !hasChange && (
                                <span className="px-2 py-0.5 bg-blue-500 bg-opacity-20 text-blue-400 text-xs rounded">
                                  Override
                                </span>
                              )}
                            </div>
                            <p className="text-text-secondary text-sm mt-0.5">{perm.description}</p>
                          </div>

                          {/* Toggle Switch */}
                          <button
                            onClick={() => handleTogglePermission(perm.code, isEnabled)}
                            className="relative w-12 h-6 rounded-full transition-colors cursor-pointer"
                            style={{ backgroundColor: isEnabled ? '#22c55e' : '#4b5563' }}
                          >
                            <span
                              className="absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform"
                              style={{ transform: isEnabled ? 'translateX(24px)' : 'translateX(0)' }}
                            />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              {permissions.length === 0 && (
                <div className="text-center py-8">
                  <Icon name="Shield" size={48} color="#9ca3af" className="mx-auto mb-4" />
                  <p className="text-text-secondary">No permissions available</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border bg-surface">
          <div className="flex items-center justify-between">
            <div className="text-text-secondary text-sm">
              {hasPendingChanges && (
                <span className="text-orange-400">
                  {Object.keys(pendingChanges).length} unsaved change(s)
                </span>
              )}
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-text-secondary hover:text-text-primary transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!hasPendingChanges || saving || !hasPermission('manage_permissions')}
                className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                  hasPendingChanges && hasPermission('manage_permissions')
                    ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-orange-600 hover:to-orange-700'
                    : 'bg-gray-600 text-text-secondary cursor-not-allowed'
                }`}
              >
                {saving ? (
                  <span className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Saving...</span>
                  </span>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PermissionManagementModal;
